import { EndpointType } from "@/constants/enums";
import { nativeEnumSchema } from "@/utils/form";
import { z } from "zod";

const phoneRegex = /^[+]?[0-9]{10,13}$/;
const vatNumberRegex = /^[A-Z]{2}[0-9A-Z]{9,13}$/;
const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/;
const bicRegex = /^[A-Z]{6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3})?$/;

const documentaryCredentialsSchema = z.object({
  endpoint: z.url("Invalid URL"),
  authEndpoint: z.url("Invalid URL"),
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
  endpointType: nativeEnumSchema(EndpointType, "Endpoint type"),
});

const invoiceDocumentaryCredentialsFormSchema = z.object({
  endpoint: z.string().optional(),
  authEndpoint: z.string().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  endpointType: nativeEnumSchema(EndpointType, "Endpoint type").optional(),
}).optional();

export const CreateMerchantSchema = z.object({
  name: z.string().min(1, "Name is required"),
  phone: z.string().regex(phoneRegex, "Invalid phone number, e.g. +31612345678"),
  vatNumber: z.string().regex(vatNumberRegex, "Invalid VAT number, e.g. NL123456789B01").optional().or(z.literal("")),
  logo: z.url("Invalid URL").optional().or(z.literal("")),
  address: z.object({
    street: z.string().min(1, "Street is required"),
    houseNumber: z.string().min(1, "House number is required"),
    zipcode: z.string().min(1, "Zip code is required"),
    city: z.string().min(1, "City is required"),
    country: z.string().min(1, "Country is required"),
  }),
  bankAccount: z.object({
    iban: z.string().regex(ibanRegex, "Invalid IBAN"),
    bic: z.string().regex(bicRegex, "Invalid BIC"),
    name: z.string().min(1, "Bank name is required"),
    beneficiaryId: z.string().optional(),
    verified: z.string().optional(),
  }),
  assetDocumentaryCredentials: documentaryCredentialsSchema,
  invoiceDocumentaryCredentials: invoiceDocumentaryCredentialsFormSchema,
  companyDetails: z.object({
    industry: z.string().optional(),
    description: z.string().optional(),
    foundingYear: z.string().optional().refine(
      (v) => !v || (Number.isInteger(Number(v)) && Number(v) >= 1900 && Number(v) <= new Date().getFullYear()),
      "Must be a whole number between 1900 and the current year"
    ),
    yearlyRevenue: z.string().optional().refine(
      (v) => !v || Number(v) >= 0,
      "Must be a non-negative number"
    ),
    website: z.url("Invalid URL").optional().or(z.literal("")),
    facebook: z.url("Invalid URL").optional().or(z.literal("")),
    twitter: z.url("Invalid URL").optional().or(z.literal("")),
    linkedin: z.url("Invalid URL").optional().or(z.literal("")),
  }),
}).superRefine((data, ctx) => {
  const inv = data.invoiceDocumentaryCredentials;
  if (!inv) return;

  const isEmpty = [
    inv.endpoint,
    inv.authEndpoint,
    inv.username,
    inv.password,
    inv.endpointType,
  ].every((field) => !field);

  if (isEmpty) return;

  const parsed = documentaryCredentialsSchema.safeParse(inv);
  if (!parsed.success) {
    parsed.error.issues.forEach((issue) => {
      ctx.addIssue({
        ...issue,
        path: ["invoiceDocumentaryCredentials", ...issue.path],
      });
    });
  }
});

export const CreateMerchantDefaultValues = {
  name: "",
  phone: "",
  vatNumber: "",
  logo: "",
  address: { street: "", houseNumber: "", zipcode: "", city: "", country: "" },
  bankAccount: { iban: "", bic: "", name: "", beneficiaryId: "", verified: "false" },
  assetDocumentaryCredentials: { endpoint: "", authEndpoint: "", username: "", password: "", endpointType: undefined },
  invoiceDocumentaryCredentials: { endpoint: "", authEndpoint: "", username: "", password: "", endpointType: undefined },
  companyDetails: { industry: "", description: "", foundingYear: undefined, yearlyRevenue: undefined, website: "", facebook: "", twitter: "", linkedin: "" },
};

export type TCreateMerchantSchema = z.infer<typeof CreateMerchantSchema>;
